import type { ButtonHTMLAttributes, JSX } from 'react'

export type ButtonTone = 'primary' | 'secondary' | 'ghost' | 'quiet' | 'danger'

/**
 * A button that can be busy.
 *
 * `data-loading` is what the Atlas layer keys its spinner off, and it keeps the
 * button's width so the layout never jumps mid-submit (S-D1). The pointer is
 * disabled by CSS, but `disabled` is set as well: CSS alone still lets the
 * Enter key fire a focused button, which is how a double-submit happens on a
 * keyboard.
 *
 * The busy label is read out rather than left to the spinner, because a spinner
 * is invisible to a screen reader.
 */
export function Button({
  tone = 'ghost',
  size,
  block,
  busy = false,
  busyLabel = 'Working',
  children,
  ...rest
}: {
  tone?: ButtonTone
  size?: 'sm' | 'lg'
  block?: boolean
  busy?: boolean
  busyLabel?: string
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'>): JSX.Element {
  const classes = [
    'ar-btn',
    `ar-btn--${tone}`,
    size ? `ar-btn--${size}` : null,
    block ? 'ar-btn--block' : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type="button"
      {...rest}
      className={classes}
      data-loading={busy ? 'true' : undefined}
      disabled={busy || rest.disabled}
    >
      {children}
      {busy ? <span className="ar-visually-hidden">{busyLabel}</span> : null}
    </button>
  )
}
